import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { useUserDashboard } from '../../hooks/useUserDashboard';
import { useInvoiceSearch } from '../../hooks/useInvoiceSearch';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import { useDispatch } from 'react-redux';
import RequestCreditInvoiceModal from './RequestCreditInvoiceModal';
import {
    TableRow,
    TableCell,
    TableHeader,
    TableColumn,
    MobileCardRow,
    MobileCardWrapper,
    TableBody,
} from './table-components';
import './../../scss/components/invoice-table-detailed.style.scss';
import { getItem } from '../../helper/localstorage.helper';
import { SearchIcon, ArrowLeftIcon, ArrowRightIcon, SendIcon, EditIcon, DeleteIcon, CheckIcon, DownloadIcon, RequestCreditIcon } from './../../assets/dashboard-icon.svg';
import { formatAmount, formatDate, getStatusBadgeClass, mapInvoiceStatus, getExpressPaymentStatus, getStageBadgeClass, handleDownloadInvoice, sentInvoicesStatus } from '../../helper/common.helper';
import { NoInvoicesYet } from './no-invoices-yet.component';

interface InvoiceTableDetailedProps {
    className?: string;
}

export const InvoiceTableDetailed: React.FC<InvoiceTableDetailedProps> = ({
    className = ''
}) => {
    const {
        dashboardData,
        loading,
        error,
        pagination,
        filters,
        fetchDashboardData,
        goToNextPage,
        goToPreviousPage,
        setCurrentPage,
        setDateTime,
        setType,
        setStatus,
        clearDateTime
    } = useUserDashboard();

    const {
        searchResults,
        loading: searchLoading,
        error: searchError,
        pagination: searchPagination,
        filters: searchFilters,
        performSearch,
        updateSearchQuery,
        clear: clearSearch,
    } = useInvoiceSearch();

    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const language = getItem('i18nextLng') || 'en-US';

    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [selectedStatus, setSelectedStatus] = useState<string>('');
    const [selectedStage, setSelectedStage] = useState<string>('');
    const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState<boolean>(false);
    const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<number | null>(null);
    const [editingInvoiceNumber, setEditingInvoiceNumber] = useState<string | null>(null);
    const [showCreditInvoiceModal, setShowCreditInvoiceModal] = useState<boolean>(false);
    const [selectedInvoiceForCredit, setSelectedInvoiceForCredit] = useState<any>(null);
    const [showVersionRestrictionModal, setShowVersionRestrictionModal] = useState<boolean>(false);
    const [selectedInvoiceVersion, setSelectedInvoiceVersion] = useState<number | null>(null);
    const user = JSON.parse(getItem('user') as string);
    const isLoginWithBankId = user?.isLoginWithBankId;
    const invoiceSearchVal = new URLSearchParams(window.location.search).get('invoiceSearch') || '';
    const currentVersion = localStorage.getItem('currentappversion') || 'v1';
    const isV4Mode = currentVersion === 'v2';

    const dropdownRef = useRef<HTMLDivElement>(null);
    const invoices = dashboardData?.response || [];

    const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        updateSearchQuery(query);

        if (!query.trim()) {
            clearSearch();
            setIsSearchDropdownOpen(false);
            return;
        }

        try {
            setIsSearchDropdownOpen(true);
            await performSearch({
                PageNumber: 1,
                PageSize: 10,
                searchQuery: query,
            });
        } catch (error: any) {
            console.error('Search error:', error);
            setIsSearchDropdownOpen(false);
        }
    };

    const handleRequestCreditInvoice = (invoice: any) => {
        setSelectedInvoiceForCredit(invoice);
        setShowCreditInvoiceModal(true);
    };

    const handleCloseCreditInvoiceModal = () => {
        setShowCreditInvoiceModal(false);
        setSelectedInvoiceForCredit(null);
    };

    const handleCreditInvoiceSuccess = () => {
        fetchDashboardData();
    };

    const handleEditInvoiceButtonClick = async (invoice: any) => {
        // Check if invoice was created with V1 and current version is V4
        const invoiceToolVersion = invoice.invoiceToolVersion || 1; // Default to 1 if not specified
        
        if (invoiceToolVersion === 1 && isV4Mode) {
            // Show restriction modal
            setSelectedInvoiceVersion(invoiceToolVersion);
            setShowVersionRestrictionModal(true);
            return;
        }

        setEditingInvoiceNumber(invoice.invoiceNumber);

        const createdDate = invoice.createdDate || new Date().toISOString();

        const params = new URLSearchParams({
            lang: language,
            invoiceNumber: invoice.invoiceNumber,
            edit: 'true',
            createdDate: createdDate
        });

        navigate(`/v2/create-invoice?${params.toString()}`);
    };

    const isInitialMount = useRef(true);

    useEffect(() => {
        clearDateTime();
        fetchDashboardData();
    }, [clearDateTime, fetchDashboardData]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            fetchDashboardData();

            if (invoiceSearchVal) {
                updateSearchQuery(invoiceSearchVal);
                performSearch({
                    PageNumber: 1,
                    PageSize: 10,
                    searchQuery: invoiceSearchVal,
                });
            }
        }
    }, []);

    useEffect(() => {
        if (isInitialMount.current) return;
        fetchDashboardData();
    }, [pagination.currentPage, pagination.pageSize]);

    useEffect(() => {
        if (isInitialMount.current) return;
        fetchDashboardData();
    }, [filters.type, filters.status]);

    const filteredInvoices = useMemo(() => {
        const sourcedInvoices = searchFilters?.searchQuery && searchResults ? searchResults : invoices;
        return sourcedInvoices;

    }, [searchFilters?.searchQuery, searchResults, invoices]);

    const PaginationSearch = useMemo(() => {
        const sourcedInvoices = searchFilters?.searchQuery && searchFilters ? searchPagination : pagination;
        return sourcedInvoices;
    }, [searchFilters?.searchQuery, searchResults, invoices]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
                setIsSearchDropdownOpen(false);
            }
        };

        if (isDropdownOpen || isSearchDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen, isSearchDropdownOpen]);


    useEffect(() => {
        if (!searchFilters.searchQuery || searchFilters.searchQuery.trim().length === 0) {
            setIsSearchDropdownOpen(false);
        }
    }, [searchFilters.searchQuery]);

    const getPageNumbers = () => {
        const { currentPage, totalPages } = PaginationSearch;
        const pages: (number | string)[] = [];

        if (totalPages <= 5) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > 3) {
                pages.push('...');
            }
            const startPage = Math.max(2, currentPage - 1);
            const endPage = Math.min(totalPages - 1, currentPage + 1);
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
            if (currentPage < totalPages - 2) {
                pages.push('...');
            }
            pages.push(totalPages);
        }

        return pages;
    };

    if (loading) {
        return (
            <div className={`invoice-table-container ${className}`}>
                <div className="invoice-table-header">
                    <h3 className="invoice-table-title">{t('MY_INVOICES')}</h3>
                </div>
                <div className="invoice-table-content">
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                        {t('LOADING_INVOICES')}
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className={`invoice-table-container ${className}`}>
            <div className="invoice-table-header">
                <h3 className="invoice-table-title">{t('MY_INVOICES')}</h3>

                <div className="invoice-table__filter-bar">
                    <div className="invoice-table__search-input" ref={dropdownRef}>
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder={t('HEADER_SEARCH') || 'Search'}
                            value={searchFilters.searchQuery}
                            onChange={handleSearchChange}
                            className="invoice-table__search-field"
                        />
                    </div>

                    <select
                        className="invoice-table__filter-select"
                        value={selectedStatus}
                        onChange={(e) => {
                            if (!e.target) return
                            const value = e.target.value;
                            setSelectedStatus(value);
                            setStatus(value ? Number(value) : undefined);
                        }}
                    >
                        <option value="">{t('SELECT_STATUS') || 'Select Status'}</option>
                        <option value="1">{t('PAID') || 'Paid'}</option>
                        <option value="2">{t('UNPAID') || 'Unpaid'}</option>
                        <option value="3">{t('PARTLY_PAID') || 'Partly Paid'}</option>
                        <option value="4">{t('OVERDUE') || 'Overdue'}</option>
                    </select>

                    <select
                        className="invoice-table__filter-select"
                        value={selectedStage}
                        onChange={(e) => {
                            if (!e.target) return;
                            const value = e.target.value;
                            setSelectedStage(value);
                            setType(value ? Number(value) : undefined);
                        }}
                    >
                        <option value="">{t('SELECT_STAGE') || 'Select Stage'}</option>
                        <option value="1">{t('SENT') || 'Sent'}</option>
                        <option value="2">{t('COMPLETED') || 'Completed'}</option>
                        <option value="3">{t('PENDING') || 'Pending'}</option>
                    </select>
                </div>
            </div>

            <div className="invoice-table-content">
                {/* Filter Bar */}


                {/* Table */}
                <div className="invoice-table-scroll-wrapper">
                    <div className="invoice-table">
                        {!isMobile && (
                            <TableHeader>
                                <TableColumn>{t('INVOICE_NO') || 'Invoice No.'}</TableColumn>
                                <TableColumn>{t('CLIENT_NAME') || 'Client Name'}</TableColumn>
                                <TableColumn>{t('AMOUNT') || 'Amount'}</TableColumn>
                                <TableColumn>{t('DUE_DATE') || 'Due Date'}</TableColumn>
                                <TableColumn>{t('STATUS_LABEL') || 'Status'}</TableColumn>
                                <TableColumn>{t('STAGE_LABEL') || 'Stage'}</TableColumn>
                                <TableColumn>{t('EXPRESS_PAYMENT_LABEL') || 'Express Payment'}</TableColumn>
                                <TableColumn>{t('ACTION_LABEL') || 'Action'}</TableColumn>
                            </TableHeader>
                        )}

                        <TableBody>
                            {filteredInvoices.length === 0 && (
                                <div className="invoice-table-content">
                                    <div className="invoice-table-scroll-wrapper">
                                        <div className="invoice-table">
                                            <div className='d-flex justify-content-center align-items-center mt-4'>
                                                <NoInvoicesYet />
                                            </div>
                                            <span className='d-flex justify-content-center pt-3'>{t('NO_INVOICES_YET')}.</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {isMobile ? (
                                // Mobile: Card-based layout
                                (Array.isArray(filteredInvoices) ? filteredInvoices : [])?.map((invoice: any) => {
                                    return (
                                    <MobileCardWrapper key={invoice.id}>
                                        <MobileCardRow label={t('INVOICE_NO') || 'Invoice No.'}>
                                            <span className="invoice-table-detailed__invoice-link">{invoice.invoiceNumber}</span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('CLIENT_NAME') || 'Client Name'}>
                                            {invoice?.customer?.name}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('AMOUNT') || 'Amount'}>
                                            {formatAmount(invoice.amount)}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('DUE_DATE') || 'Due Date'}>
                                            {formatDate(invoice.dueDate)}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('STATUS_LABEL') || 'Status'}>
                                            <span className={getStatusBadgeClass(invoice.invoiceStatus)}>
                                                {invoice.invoiceStatus === 2 && <CheckIcon />}
                                                {t(mapInvoiceStatus(invoice.invoiceStatus)) || mapInvoiceStatus(invoice.invoiceStatus)}
                                            </span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('STAGE_LABEL') || 'Stage'}>
                                            {(() => {
                                                const stageStatus = sentInvoicesStatus(invoice);
                                                return (
                                                    <span className={getStageBadgeClass(stageStatus)}>
                                                        {t(stageStatus)}
                                                    </span>
                                                );
                                            })()}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('EXPRESS_PAYMENT_LABEL') || 'Express Payment'}>
                                            {(() => {
                                                const expressStatus = getExpressPaymentStatus(invoice, isLoginWithBankId, t);
                                                return (
                                                    <button
                                                        title={expressStatus.title}
                                                        className={`invoice-table-detailed__extra-info ${!expressStatus.isDisabled ? 'invoice-table-detailed__extra-info--enabled' : ''}`}
                                                        disabled={expressStatus.isDisabled}
                                                    >
                                                        {expressStatus.icon == 'Approved' ? t('APPROVED') : t('NOTAPPLIED')}
                                                    </button>
                                                );
                                            })()}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('ACTION_LABEL') || 'Action'}>
                                            <div className="invoice-table-detailed__action-group">
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title={
                                                        invoice.isEditInvoiceRequested && invoice.isEditInvoiceRequestApproved
                                                            ? t("INVOICE_EDIT_APPROVED")
                                                            : invoice.isEditInvoiceRequested && invoice.isEditInvoiceRequestRejected
                                                                ? t("INVOICE_EDIT_REJECTED")
                                                                : invoice.isEditInvoiceRequested
                                                                    ? t("INVOICE_EDIT_PENDING")
                                                                    : t("EDIT_INVOICE")
                                                    } onClick={() => handleEditInvoiceButtonClick(invoice)}
                                                    disabled={invoice.isEditInvoiceRequested || invoice.isHaveCreditInvoice || invoice.isCreditInvoiceRequested}
                                                >
                                                    <EditIcon
                                                        color={invoice.isEditInvoiceRequested && invoice.isEditInvoiceRequestApproved
                                                            ? "green"
                                                            : invoice.isEditInvoiceRequested && invoice.isEditInvoiceRequestRejected
                                                                ? "red"
                                                                : undefined}
                                                        opacity={
                                                            !!invoice.isEditInvoiceRequested && !invoice.isEditInvoiceRequestApproved && !invoice.isEditInvoiceRequestRejected
                                                                ? 0.5
                                                                : invoice?.isHaveCreditInvoice ? 0.5 : invoice?.isCreditInvoiceRequested ? 0.5 : 1
                                                        }
                                                    />                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title="Download"
                                                    onClick={() => {
                                                        const invoceLang = invoice.customer?.country === "Sweden" ? 'sv-se' : 'en-us';
                                                        handleDownloadInvoice(invoice.invoiceNumber, invoceLang);
                                                    }}
                                                    disabled={downloadingInvoiceId === invoice.id}
                                                >
                                                    <DownloadIcon />

                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title={invoice?.isHaveCreditInvoice ? t("CREDIT_NOTE_ALREADY_REQUESTED") : t("REQUEST_CREDIT_INVOICE")}
                                                    disabled={invoice?.isHaveCreditInvoice || invoice.isEditInvoiceRequested || invoice?.isSalaryCreated}
                                                    onClick={() => handleRequestCreditInvoice(invoice)}
                                                >
                                                    <RequestCreditIcon opacity={invoice?.isHaveCreditInvoice ? 0.5 : invoice.isEditInvoiceRequested ? 0.5 : invoice?.isSalaryCreated ? 0.5 : 1} />
                                                </button>
                                            </div>
                                        </MobileCardRow>
                                    </MobileCardWrapper>
                                    )
})
                            ) : (
                                (filteredInvoices as any[]).map((invoice: any) => {
                                    return(
                                    <TableRow key={invoice.id}>
                                        <TableCell>
                                            <span className="invoice-table-detailed__invoice-link">{invoice.invoiceNumber}</span>
                                        </TableCell>
                                        <TableCell>{invoice?.customer?.name ?? invoice.clientName}</TableCell>
                                        <TableCell>{formatAmount(invoice.amount)}</TableCell>
                                        <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                                        <TableCell>
                                            <span className={`${getStatusBadgeClass(invoice?.invoiceStatus)} gap-3 d-flex align-items-center`}>
                                                {/* {invoice.invoiceStatus === 1 &&

                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width={12}
                                                        height={12}
                                                        viewBox="0 0 12 12"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M9 3L3 9M9 9.00001L3 3.00001"
                                                            stroke="#252525"
                                                            strokeWidth="1.33"
                                                            strokeLinecap="round"
                                                        />
                                                    </svg>
                                                } */}

                                                {/* {invoice.invoiceStatus === 2 && <CheckIcon />} */}
                                                {t(mapInvoiceStatus(invoice.invoiceStatus)) || mapInvoiceStatus(invoice.invoiceStatus)}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                const stageStatus = sentInvoicesStatus(invoice);
                                                return (
                                                    <span className={getStageBadgeClass(stageStatus)}>
                                                        {t(stageStatus)}
                                                    </span>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                const expressStatus = getExpressPaymentStatus(invoice, isLoginWithBankId, t);
                                                return (
                                                    <button
                                                        title={expressStatus.title}
                                                        className={`invoice-table-detailed__extra-info ${!expressStatus.isDisabled ? 'invoice-table-detailed__extra-info--enabled' : ''}`}
                                                        disabled={expressStatus.isDisabled}
                                                    >
                                                        {expressStatus.icon == 'Approved' ? t('APPROVED') : t('NOTAPPLIED')}
                                                    </button>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            <div className="invoice-table-detailed__action-group">
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title={
                                                        invoice.isEditInvoiceRequested && invoice.isEditInvoiceRequestApproved
                                                            ? t("INVOICE_EDIT_APPROVED")
                                                            : invoice.isEditInvoiceRequested && invoice.isEditInvoiceRequestRejected
                                                                ? t("INVOICE_EDIT_REJECTED")
                                                                : invoice.isEditInvoiceRequested
                                                                    ? t("INVOICE_EDIT_PENDING")
                                                                    : t("EDIT_INVOICE")
                                                    }
                                                    onClick={() => handleEditInvoiceButtonClick(invoice)}
                                                    disabled={invoice.isEditInvoiceRequested || invoice.isHaveCreditInvoice || invoice.isCreditInvoiceRequested}
                                                >
                                                    <EditIcon
                                                        color={invoice.isEditInvoiceRequested && invoice.isEditInvoiceRequestApproved
                                                            ? "green"
                                                            : invoice.isEditInvoiceRequested && invoice.isEditInvoiceRequestRejected
                                                                ? "red"
                                                                : undefined}
                                                        opacity={
                                                            !!invoice.isEditInvoiceRequested && !invoice.isEditInvoiceRequestApproved && !invoice.isEditInvoiceRequestRejected
                                                                ? 0.5
                                                                : invoice?.isHaveCreditInvoice ? 0.5 : invoice?.isCreditInvoiceRequested ? 0.5 : 1
                                                        }
                                                    />
                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title="Download"
                                                    onClick={() => {
                                                        const invoceLang = invoice.customer?.country == "Sweden" ? 'sv-se' : 'en-us';
                                                        handleDownloadInvoice(invoice.invoiceNumber, invoceLang);
                                                    }}
                                                    disabled={downloadingInvoiceId === invoice.id}
                                                >
                                                    <DownloadIcon />
                                                </button>

                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    title={invoice?.isHaveCreditInvoice ? t("CREDIT_NOTE_ALREADY_REQUESTED") : t("REQUEST_CREDIT_INVOICE")}
                                                    disabled={invoice?.isHaveCreditInvoice || invoice.isEditInvoiceRequested || invoice?.isSalaryCreated}
                                                    onClick={() => handleRequestCreditInvoice(invoice)}
                                                >
                                                    <RequestCreditIcon opacity={invoice?.isHaveCreditInvoice ? 0.5 : invoice.isEditInvoiceRequested ? 0.5 : invoice?.isSalaryCreated ? 0.5 : 1} />
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                    )}
                                )
                            )}
                        </TableBody>
                    </div>
                </div>

                {/* Pagination */}
                {PaginationSearch.totalPages > 0 && (
                    <div className="invoice-table-pagination">
                        <div className="pagination-controls">
                            <button
                                className="pagination-button pagination-button--prev"
                                onClick={() => goToPreviousPage()}
                                disabled={PaginationSearch.currentPage === 1}
                            >
                                <ArrowLeftIcon />
                                <span>{t('PREV_STEP') || 'Prev'}</span>
                            </button>

                            {getPageNumbers().map((page, index) => {
                                if (page === '...') {
                                    return (
                                        <button
                                            key={`ellipsis-${index}`}
                                            className="pagination-button pagination-button--number"
                                            disabled
                                        >
                                            ...
                                        </button>
                                    );
                                }
                                return (
                                    <button
                                        key={page}
                                        className={`pagination-button pagination-button--number${page === PaginationSearch.currentPage ? ' pagination-button--active' : ''
                                            }`}
                                        onClick={() => setCurrentPage(page as number)}
                                    >
                                        {page}
                                    </button>
                                );
                            })}

                            <button
                                className="pagination-button pagination-button--next"
                                onClick={() => goToNextPage()}
                                disabled={PaginationSearch.currentPage === PaginationSearch.totalPages}
                            >
                                <span>{t('NEXT_STEP') || 'Next'}</span>  
                                <ArrowRightIcon />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Version Restriction Modal */}
            <Modal
                show={showVersionRestrictionModal}
                onHide={() => setShowVersionRestrictionModal(false)}
                centered
                className="version-restriction-modal"
                // size="sm"
                // style={{ '--bs-modal-width': '200px' } as React.CSSProperties}
                // style={{width:"20% !important",}}
            >
                <Modal.Header closeButton={false} style={{ border: 'none', paddingBottom: '0' }}>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowVersionRestrictionModal(false);
                        }}
                        aria-label="Close"
                        style={{
                            position: 'absolute',
                            right: '20px',
                            top: '20px',
                            background: '#7C3AED',
                            border: '1px solid #7C3AED',
                            cursor: 'pointer',
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '25px',
                            transition: 'none',
                            boxShadow: 'none',
                            zIndex: 1000
                        }}
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M15 5L5 15M5 5L15 15"
                                stroke="#FFFFFF"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                </Modal.Header>
                <Modal.Body style={{ padding: '2rem 2.5rem', textAlign: 'center' }}>
                    <h4 style={{
                        fontSize: '1.25rem',
                        fontWeight: '600',
                        color: '#252525',
                        marginBottom: '0.75rem',
                        lineHeight: '1.4'
                    }}>
                        {t('VERSION_RESTRICTION_TITLE') || 'Version Restriction'}
                    </h4>
                    <p style={{
                        fontSize: '0.95rem',
                        color: '#666',
                        lineHeight: '1.6',
                        marginBottom: '0',
                        maxWidth: '400px',
                        margin: '0 auto'
                    }}>
                        {t('VERSION_RESTRICTION_MESSAGE') || 'This invoice was created with Version 1 of the invoice tool. To edit this invoice, please switch to Version 1.'}
                    </p>
                </Modal.Body>
                <Modal.Footer style={{
                    border: 'none',
                    padding: '0 2.5rem 2rem',
                    justifyContent: 'center'
                }}>
                    <button
                        type="button"
                        onClick={() => setShowVersionRestrictionModal(false)}
                        style={{
                            backgroundColor: '#7C3AED',
                            color: 'white',
                            border: 'none',
                            borderRadius: '25px',
                            padding: '0.65rem 2rem',
                            fontSize: '0.95rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'none',
                            minWidth: '120px'
                        }}
                    >
                        {t('CLOSE') || 'Close'}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Request Credit Invoice Modal */}
            <RequestCreditInvoiceModal
                show={showCreditInvoiceModal}
                invoice={selectedInvoiceForCredit}
                onClose={handleCloseCreditInvoiceModal}
                onSuccess={handleCreditInvoiceSuccess}
            />
        </div >
    );
};
